/**
 * Generic error screen. Owns its own background + card chrome: the section
 * paints `--light-bg`, drops the spiral at 0.08 opacity behind a white
 * rounded card, and centers the logo/icon/title/message/retry.
 *
 * Copy can be supplied via explicit props, via `navigate('/error', { state })`,
 * or inferred from a router `errorElement`. Missing fields fall back to
 * generic auth copy.
 */
import { useEffect, useState, type MouseEvent } from 'react';
import { useLocation, useRouteError } from 'react-router-dom';
import styles from './error.module.css';

export interface ErrorPageProps {
  title?: string;
  message?: string;
  technicalDetails?: string;
  /** When true, render the retry countdown button (used for auth loops). */
  showRetry?: boolean;
  /** Override the retry destination; defaults to `/login`. */
  retryHref?: string;
}

function formatRouteError(err: unknown): { message: string; details?: string } {
  if (err instanceof Error) {
    return { message: err.message, details: err.stack };
  }
  if (typeof err === 'string') return { message: err };
  if (err && typeof err === 'object' && 'statusText' in err) {
    const { status, statusText } = err as { status?: number; statusText?: string };
    return { message: statusText || `Error ${status ?? 'unknown'}` };
  }
  return { message: 'An unexpected error occurred.' };
}

export default function ErrorPage(props: ErrorPageProps = {}): JSX.Element {
  const location = useLocation();
  const routeError = useRouteError();

  // Precedence: explicit props > router state > router error > defaults.
  const state = (location.state as ErrorPageProps | null) ?? null;
  const routerErr = routeError ? formatRouteError(routeError) : null;

  const title = props.title ?? state?.title ?? 'Authentication Error';
  const message =
    props.message ??
    state?.message ??
    routerErr?.message ??
    'An error occurred during authentication.';
  const technicalDetails =
    props.technicalDetails ?? state?.technicalDetails ?? routerErr?.details;
  const showRetry = props.showRetry ?? state?.showRetry ?? false;
  const retryHref = props.retryHref ?? state?.retryHref ?? '/login';

  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (!showRetry) return;
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [showRetry, countdown]);

  const retryDisabled = showRetry && countdown > 0;

  const handleRetry = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    try {
      sessionStorage.clear();
    } catch {
      // Private mode / SSR — storage access can throw.
    }
    window.location.href = retryHref;
  };

  return (
    <section className={styles.hero}>
      <div className={styles.heroImage}>
        <img
          src="https://fulcrumasset.com/wp-content/uploads/2024/08/Fulcrum-hero-spiral.svg"
          alt="Fulcrum spiral background"
        />
      </div>
      <div className={styles.container} role="alert" aria-live="assertive">
        <div className={styles.logo}>
          <img src="/static/logos/logo.png" alt="Fulcrum Asset Management" />
        </div>
        <div aria-hidden className={styles.icon}>⚠️</div>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.message}>{message}</p>

        {technicalDetails ? (
          <div className={styles.technicalDetails}>
            <strong>Technical Details:</strong>
            <br />
            {technicalDetails}
          </div>
        ) : null}

        {showRetry ? (
          <>
            <button
              type="button"
              className={styles.btn}
              onClick={handleRetry}
              disabled={retryDisabled}
            >
              {retryDisabled ? `Try Again (${countdown})` : 'Try Again'}
            </button>
            <p className={styles.countdown}>
              Please wait before trying again to avoid redirect loops
            </p>
          </>
        ) : (
          <a href={retryHref} className={styles.btn}>
            Try Again
          </a>
        )}
      </div>
    </section>
  );
}
